import { useState } from 'react';
import { Dropdown } from '@repo/ui/Dropdown';

import { useQuery } from '@apollo/client/react';
import { Dialog } from '@repo/ui/Dialog';

import { CopyPasteField } from '@repo/ui/CopyPasteField';

import { PaginatedAccountCombobox } from './PaginatedAccountCombobox';
import { useUser } from '@repo/hooks/useUser';
import GET_SUB_ACCOUNTS_FOR_ACCOUNT from '../gql/getSubAccountsForAccount.gql';

import { POLICIES } from '@/lib/Authorization';
import { useAccount } from '@/hooks/useAccount';

import { Link, useLocation, useNavigate } from 'react-router';
import type { Mercury } from '@repo/types/Models';

const AccountSwitcher = () => {
  const currentAccount = useAccount();

  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  const user = useUser();
  const accountId = currentAccount?.id;
  const parentAccountId = currentAccount?.parentAccount?.id ?? accountId;

  const redirectPath = pathname.split(accountId)[1] ?? '';

  const PAGE_SIZE = 3;

  const menuItems = [
    accountId !== parentAccountId
      ? {
          label: 'View parent account',
          onClick: () => navigate(`/a/${parentAccountId}${redirectPath}`),
        }
      : null,

    POLICIES['accounts:index'](user)
      ? {
          label: 'View all sub accounts',
          onClick: () => navigate(`/a/${accountId}/sub-accounts`),
        }
      : null,

    POLICIES['accounts:create'](user)
      ? {
          label: 'Find and manage sub account',
          onClick: () => setIsOpenSubAccountDialog(true),
        }
      : null,
  ];

  const [queryVars, setQueryVars] = useState<Mercury.GQL.QueryVariables | null>({
    first: PAGE_SIZE,
    op: 'FUZZ',
  });

  const { loading, error, data } = useQuery(GET_SUB_ACCOUNTS_FOR_ACCOUNT, {
    variables: {
      id: accountId,
      ...queryVars,
    },
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    // Error handling could be improved here
  }

  const { edges: accountNodes, pageInfo } = data?.subAccountsForAccount ?? {};

  const accountItems = accountNodes?.map((account) => account.node) ?? [];

  const items = accountItems?.map((account) => {
    return {
      label: account?.name,
      href: `/a/${account.id}${redirectPath}`,
    };
  });

  const [isOpenSubAccountDialog, setIsOpenSubAccountDialog] = useState<boolean>(false);

  const badgeClasses =
    'px-2 text-xs sm:px-5 mx-auto sm:text-sm text-center rounded-full w-min whitespace-nowrap';

  if (currentAccount?.parentAccount) {
    return (
      <div className="flex gap-2 items-center w-full sm:gap-5">
        <span
          className="text-sm font-bold truncate whitespace-nowrap sm:text-xl max-w-[24ch]"
          title={currentAccount?.name}
        >
          {currentAccount?.name}
        </span>
        <div className="h-[50px] w-[1px] bg-grey-400/50"></div>
        <CopyPasteField label="Customer ID" text={currentAccount?.customerId?.toString()} />
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-2 items-center w-full">
        <Dropdown
          items={menuItems.filter((item): item is NonNullable<typeof item> => item !== null)}
          align="start"
          icon="expand_all"
          variant="button"
        />

        <div className="flex gap-2 items-center w-full sm:gap-5">
          <div className="flex flex-col gap-1">
            <Link
              to={`/a/${currentAccount?.id}${redirectPath}`}
              className="text-sm font-bold truncate whitespace-nowrap sm:text-xl hover:underline max-w-[24ch]"
              title={currentAccount?.name}
            >
              <span> {currentAccount?.name}</span>
            </Link>

            {currentAccount?.parentAccount ? (
              <div className={`${badgeClasses} text-orange-800 bg-orange-200`}>Sub account</div>
            ) : (
              <div className={`${badgeClasses} text-purple-800 bg-purple-200`}>Parent account</div>
            )}
          </div>
          <div className="hidden gap-2 items-center w-full md:flex sm:gap-5">
            <div className="h-[50px] w-[1px] bg-grey-400/50"></div>

            <CopyPasteField label="Customer ID" text={currentAccount?.customerId?.toString()} />
          </div>
        </div>
      </div>

      <Dialog
        title="Find sub account"
        isOpen={isOpenSubAccountDialog}
        setIsOpen={setIsOpenSubAccountDialog}
      >
        <PaginatedAccountCombobox
          options={items}
          pageSize={PAGE_SIZE}
          pageInfo={pageInfo}
          setQueryVars={setQueryVars}
          queryVars={queryVars}
          loading={loading}
          op="FUZZ"
        />
      </Dialog>
    </>
  );
};

export default AccountSwitcher;
